import { useIntl } from 'react-intl';
import { ButtonUI, EmptyState } from 'sonata-react-components';

import './selectionSummaryPanel.scss';

import type { PoiWithUplift } from '@/services/footfall.service';

type Props = {
  selectedPois: PoiWithUplift[];
  onRemove: (venueId: PoiWithUplift['venueId']) => void;
  onClearAll: () => void;
};

export function SelectionSummaryPanel({ selectedPois, onRemove, onClearAll }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  return (
    <div className="selectionSummaryPanel">
      {selectedPois.length === 0 ? (
        <EmptyState
          description={formatMessage({
            defaultMessage: 'Select POIs from the list to add them to your report.',
            id: 'driveToStore.poiSelectionModal.selectionSummary.emptyStateDescription'
          })}
          icon="fal fa-empty-set"
          show
          title={formatMessage({
            defaultMessage: 'No POIs selected yet',
            id: 'driveToStore.poiSelectionModal.selectionSummary.emptyStateTitle'
          })}
        />
      ) : (
        <>
          <div className="selectionSummaryPanel__header">
            <p className="selectionSummaryPanel__title">
              <span className="selectionSummaryPanel__titleCount">{selectedPois.length}</span>
              <span className="selectionSummaryPanel__titleCountLabel">
                {selectedPois.length === 1
                  ? formatMessage({ defaultMessage: 'POI', id: 'driveToStore.poiSelectionModal.selectionSummary.poi' })
                  : formatMessage({
                      defaultMessage: 'POIs',
                      id: 'driveToStore.poiSelectionModal.selectionSummary.pois'
                    })}
              </span>
            </p>
          </div>
          <div className="selectionSummaryPanel__list">
            {selectedPois.map(poi => (
              <div className="selectionSummaryPanel__item" key={`selection-summary-${poi.venueId}`}>
                <div className="selectionSummaryPanel__itemDetails">
                  <p className="selectionSummaryPanel__itemName" title={poi.name}>
                    {poi.name}
                  </p>
                  <p className="selectionSummaryPanel__itemAddress" title={poi.address}>
                    {poi.address}
                  </p>
                </div>
                <ButtonUI
                  icon="fa-solid fa-xmark"
                  iconClassName="selectionSummaryPanel__itemRemove"
                  id={`selectionSummaryPanel-remove-${poi.venueId}`}
                  onClick={() => onRemove(poi.venueId)}
                  shape="link"
                  size="ultraSmall"
                  title={formatMessage({
                    defaultMessage: 'Remove',
                    id: 'driveToStore.poiSelectionModal.selectionSummary.remove'
                  })}
                  type="secondary"
                />
              </div>
            ))}
          </div>
          <div className="selectionSummaryPanel__footer">
            <ButtonUI
              disabled={selectedPois.length === 0}
              icon="fa-regular fa-trash"
              id="selectionSummaryPanel-clearAll"
              onClick={onClearAll}
              shape="link"
              size="ultraSmall"
              text={formatMessage({
                defaultMessage: 'Clear selection',
                id: 'driveToStore.poiSelectionModal.clearSelection'
              })}
              type="secondary"
            />
          </div>
        </>
      )}
    </div>
  );
}
